import * as React from 'react';
// Import material ui modules
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import services.
import {
	PerilService,
} from '@app/services';
// Import hooks.
import {
	UseInputFormResult,
	useInputPerilsSelectionList,
	UseInputPerilsSelectionListResults,
	useInputYesNo,
	UseInputYesNoResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupAllPerilOptionsIneligibleNotificationProps,
	FormPopupConfirmationProps,
	FormPopupIneligibleSelectedPerilsNotificationProps,
	FormProps,
	NavigationFooterProps,
	FormPerilsProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';
// Import api models.
import {
	PerilDeductible,
    PossiblePeril,
} from '@app/models/api';

interface UseFormPerilsParameters {
	id: string;
	props: FormPerilsProps,
	showValidations?: boolean;
}

interface UseFormPerilsResults {
	submitForm: () => void;
	isTriaQuestionVisible: () => boolean;
	onViewNotice: () => void;
	hasNotice: boolean;
	perilsSelectionListInput: UseInputPerilsSelectionListResults;
	includeTriaCoverageInput: UseInputYesNoResults;
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applySubmissionFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyFormPopupIneligibleSelectedPerilsNotificationProps: () => FormPopupIneligibleSelectedPerilsNotificationProps;
	applyFormPopupAllPerilOptionsIneligibleNotificationProps: () => FormPopupAllPerilOptionsIneligibleNotificationProps;
}

const useFormPerils = (params: UseFormPerilsParameters): UseFormPerilsResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			if (requireConfirmationOnSave) {
				setOpenSaveConfirmationPopup(true);
			}
			else {
				params.props.onSavePerils(perilsSelectionListInput.value, allowTriaCoverageQuestion() ? includeTriaCoverageInput.value : undefined);
			}			
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Perils',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isSaveRequired ? 'Save' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the submission confirmation popup form. 
	 **/
	const applySubmissionFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openSaveConfirmationPopup,
			onClose: cancelConfirmSubmission,
			onConfirm: confirmSubmission,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the invalid initial selected perils popup form. 
	 **/
	const applyFormPopupIneligibleSelectedPerilsNotificationProps = (): FormPopupIneligibleSelectedPerilsNotificationProps => {
		return {
			isOpen: openIneligiblePerilsSelectedNoticePopup,
			onClose: closeIneligiblePerilsSelectedNoticePopup,
			selectedIneligiblePerils: ineligibleSelectedPerils,
		};
	};

	/**
	 * Returns props for the form-popup-all-peril-options-ineligible-notification-props. 
	 **/
	const applyFormPopupAllPerilOptionsIneligibleNotificationProps = (): FormPopupAllPerilOptionsIneligibleNotificationProps => {
		return {
			isOpen: openAllPerilOptionsIneligibleNotificationPopup,
			onClose: closeAllPerilOptionsIneligibleNoticePopup,
			ineligiblePerilOptions: nonDeprecatedPossiblePerils,
		};
	};

	/**
	 * Checks to see if the tria question is hidden
	 **/
	const isTriaQuestionVisible = (): boolean => {
		return allowTriaCoverageQuestion() && perilsSelectionListInput.value.length > 1;
	}

	/**
	 * Updates the flag to render the the notice popup.
	 */
	const onViewNotice = (): void => {
		if (areAllNonDeprecatedPerilOptionsIneligible) {
			setOpenAllPerilOptionsIneligibleNotificationPopup(() => true);
		}
		else if (areAnySelectedPerilsIneligible) {
			setOpenIneligiblePerilsSelectedNoticePopup(() => true);
		}
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if broad line of business is commercial. 
	 **/
	const allowTriaCoverageQuestion = (): boolean => {
		return params.props.allowTriaCoverageQuestion ?? false;
	}

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	/**
	 * Confirms submission if user chooses to. 
	 **/
	const confirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
		params.props.onSavePerils(perilsSelectionListInput.value, allowTriaCoverageQuestion() ? includeTriaCoverageInput.value : undefined);
	}

	/**
	 * Cancels the confirmation prompt for submission. 
	 **/
	const cancelConfirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
	}

	/**
	 * Closes the ineligible-perils-selected notice popup. 
	 **/
	const closeIneligiblePerilsSelectedNoticePopup = (): void => {
		setOpenIneligiblePerilsSelectedNoticePopup(false);
	}

	/**
	 * Closes the all-peril-options-ineligible notice popup
	 **/
	const closeAllPerilOptionsIneligibleNoticePopup = (): void => {
		setOpenAllPerilOptionsIneligibleNotificationPopup(false);
	}

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<PerilDeductible[]>(perilsSelectionListInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(includeTriaCoverageInput, focusToElement);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		perilsSelectionListInput.changeShowValidationFlag(true);
		includeTriaCoverageInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return perilsSelectionListInput.doesInputHaveChanges
			|| includeTriaCoverageInput.doesInputHaveChanges;
	}

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the perils. 
	 **/
	const generateIdForPerils = (): string => {
		return `${params.id}-perils`;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for the include tria coverage. 
	 **/
	const generateIdForIncludeTriaCoverage = (): string => {
		return `${generateIdForForm()}-includeTriaCoverage`;
	}

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return params.props.allowEdits
			&& !areAllNonDeprecatedPerilOptionsIneligible
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	/**
	 * Initializes the selected perils.
	 * @param selectedPerils
	 */
	const createInitialSelectedPerils = (selectedPerils: PerilDeductible[]): PerilDeductible[] => {
		return PerilService.getInstance().correctPerilDeductibles([...selectedPerils], params.props.possiblePerils);
	}

	// #endregion private methods


	// #region private variables

	let requireConfirmationOnSave: boolean = React.useMemo(() => params.props.requireConfirmationOnSave ?? false, [params.props.requireConfirmationOnSave]);

	let ineligibleSelectedPerils: PossiblePeril[] = React.useMemo(() => {
		return PerilService.getInstance().getIneligibleSelectedPerils(params.props.selectedPerils, params.props.possiblePerils);
	}, [params.props.selectedPerils, params.props.possiblePerils]);

	let areAnySelectedPerilsIneligible: boolean = React.useMemo(() => {
		return ineligibleSelectedPerils.length > 0 && params.props.allowEdits;
	}, [ineligibleSelectedPerils]);

	let areAllNonDeprecatedPerilOptionsIneligible: boolean = React.useMemo(() => {
		return PerilService.getInstance().areAllNonDeprecatedPossiblePerilsIneligible(params.props.possiblePerils);
	}, [params.props.possiblePerils]);

	let nonDeprecatedPossiblePerils: PossiblePeril[] = React.useMemo(() => {
		return PerilService.getInstance().getNonDeprecatedIneligiblePerils(params.props.possiblePerils);
	}, [params.props.possiblePerils]);

	let hasNotice: boolean = React.useMemo(() => {
		return areAnySelectedPerilsIneligible || areAllNonDeprecatedPerilOptionsIneligible;
	}, [areAnySelectedPerilsIneligible, areAllNonDeprecatedPerilOptionsIneligible]);

	// Setup the base form. 
	const perilsSelectionListInput: UseInputPerilsSelectionListResults = useInputPerilsSelectionList({
		id: generateIdForPerils(),
		label: 'Select the peril(s) you would like to insure against',
		required: true,
		disabled: !params.props.allowEdits,
		selectedPerils: createInitialSelectedPerils(params.props.selectedPerils),
		possiblePerils: params.props.possiblePerils,
		broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
		showValidation: params.showValidations,
	});

	let includeTriaCoverageAnswer: boolean = params.props.includeTriaCoverage ?? false;

	// Instantiate the 'include tria coverage' input.
	const includeTriaCoverageInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForIncludeTriaCoverage(),
		label: 'Do you wish to be covered by act of terrorism?',
		required: true,
		disabled: !params.props.allowEdits || !isTriaQuestionVisible(),
		isYesChecked: isTriaQuestionVisible()
			? includeTriaCoverageAnswer
			: false,
		showValidation: params.showValidations,
	});

	const [openSaveConfirmationPopup, setOpenSaveConfirmationPopup] = React.useState<boolean>(false)
	const [openIneligiblePerilsSelectedNoticePopup, setOpenIneligiblePerilsSelectedNoticePopup] = React.useState<boolean>(areAnySelectedPerilsIneligible && !areAllNonDeprecatedPerilOptionsIneligible)
	const [openAllPerilOptionsIneligibleNotificationPopup, setOpenAllPerilOptionsIneligibleNotificationPopup] = React.useState<boolean>(areAllNonDeprecatedPerilOptionsIneligible)
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// #endregion private variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidation(true)) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			perilsSelectionListInput.focusToInput();
		}
	}, []);

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& params.props.statusMessage !== undefined
			&& params.props.allowEdits
			&& checkFormValidation(false)) {
			setShowStatusMessage(() => {
				return true;
			});
		}
		else {
			setShowStatusMessage(() => {
				return false;
			});
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, params.props.allowEdits]);

	// Called when updates are made to the perils.
	React.useEffect(() => {
		if (allowTriaCoverageQuestion()
			&& perilsSelectionListInput.value.length < 2) {
			includeTriaCoverageInput.setInput(false);
		}
	}, [perilsSelectionListInput.value]);

	// #endregion life-cycle methods.


	return {
		submitForm,
		isTriaQuestionVisible,
		onViewNotice,
		hasNotice,
		perilsSelectionListInput,
		includeTriaCoverageInput,
		applyFormProps,
		applyNavigationFooterProps,
		applySubmissionFormPopupConfirmationProps,
		applyFormPopupIneligibleSelectedPerilsNotificationProps,
		applyFormPopupAllPerilOptionsIneligibleNotificationProps,
	};
}

// Export parameters.
export {
	UseFormPerilsParameters,
}

// Export results.
export {
	UseFormPerilsResults,
}

export default useFormPerils;